import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom-v5-compat";
import { useHotelsConfig } from "@b2bportal/core-hotels";
import type { AppDispatch } from "@b2bportal/core-types";
import { arraysAreEqual } from "@b2bportal/core-utilities";
import { StayType } from "@b2bportal/lodging-api";
import { getLang } from "@hopper-b2b/i18n";
import {
  ensureFunnel,
  getFunnelId,
  HotelNavigationUtility,
} from "@overrides/utilities";
import dayjs from "dayjs";
import {
  HotelsShopSelectors,
  HotelShopThunk,
  type ShopSearch,
  HotelsShopActions,
} from "../store";
import type { ShopRouteProps } from "../types";
import { funnelIdCache } from "~core-hotels/utilities";

const inheritedFunnelId = (search: ShopSearch): string | undefined => {
  try {
    return funnelIdCache.get({
      lodgingId: search.lodgingId,
      fromDate: search.dateRange.from,
      untilDate: search.dateRange.until,
      guests: search.guests,
      rooms: search.rooms,
    });
  } catch {
    // localStorage unavailable; the shop mints its own funnel as before.
    return undefined;
  }
};

export const useFetchHotelShop = ({
  lodgingId,
  adultsCount: adults,
  childrenAges: children,
  fromDate,
  untilDate,
  roomsCount,
  lodgingSelection,
  stayType = StayType.Hotels,
}: ShopRouteProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const search = useSelector(HotelsShopSelectors.getShopSearch);
  const rooms = roomsCount ?? 1;
  const config = useHotelsConfig();
  const currentLocale = getLang();
  const location = useLocation();

  const isHomes = stayType === StayType.Homes;

  const urlSearch: ShopSearch = useMemo(() => {
    return {
      dateRange: { from: fromDate, until: untilDate },
      guests: { adults, children: children ?? [] },
      rooms,
      lodgingId,
      locale: currentLocale,
    };
  }, [fromDate, untilDate, adults, children, rooms, lodgingId, currentLocale]);

  // Ensure the funnel ID is set before we request shop, so there is a funnel ID
  // to link to the offers returned in the shop response. Without it, those
  // offers are not correctly linked to the booking session.
  //
  // On desktop the hotel list opens this page in a new tab, whose
  // sessionStorage has no funnel. The list hands the search's funnel over
  // through `funnelIdCache` (same key as the shop token), so the hotel stays
  // attributed to the search it was opened from instead of minting its own.
  useEffect(() => {
    ensureFunnel(
      "lodging",
      HotelNavigationUtility.getHotelShopFunnelSearch(location.search),
      inheritedFunnelId(urlSearch)
    );
  }, [location.search, urlSearch]);

  useEffect(() => {
    // Read after `ensureFunnel` above has seeded the funnel. The shop response
    // carries the fintech offers, attributed to the funnel active when it was
    // requested, so a shop fetched under a funnel that has since rotated (e.g.
    // a new search committed from the home page) is stale even when the stay
    // search itself is unchanged: its offers could not be joined to a booking
    // quoted under the current funnel.
    const funnelId = getFunnelId("lodging");
    const shouldUpdateSearch =
      search == null ||
      search.locale !== currentLocale ||
      search.guests.adults !== adults ||
      !arraysAreEqual(search.guests.children, children ?? []) ||
      !dayjs(search.dateRange.from).isSame(fromDate, "date") ||
      !dayjs(search.dateRange.until).isSame(untilDate, "date") ||
      search.rooms !== rooms ||
      search.lodgingId !== lodgingId ||
      search.funnelId !== funnelId;

    if (shouldUpdateSearch) {
      const nextSearch: ShopSearch = { ...urlSearch, funnelId };
      dispatch(HotelsShopActions.setShopSearch(nextSearch));
      if (isHomes) {
        dispatch(HotelShopThunk.fetchHomesShop({ search: nextSearch, config }));
      } else {
        dispatch(HotelShopThunk.fetchHotelsShop({ search: nextSearch }));
        if (lodgingSelection != null) {
          dispatch(HotelsShopActions.setLodgingSelection(lodgingSelection));
        }
      }
    }
  }, [
    urlSearch,
    search,
    adults,
    children,
    fromDate,
    untilDate,
    rooms,
    lodgingId,
    dispatch,
    lodgingSelection,
    isHomes,
    config,
    currentLocale,
  ]);
};
